import {
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { formatDateTime } from '@/domains/survey';
import { oversightApi } from '../api/oversight-api';
import type { IPage, ITimelineRow, TimelineSource } from '../api/oversight-contracts';
import { LoadError, Pager, TableSkeleton, useAdminData } from '../components/admin-page';
import { withBatch } from '../components/history-batch';
import { HiddenRows } from '../components/history-popover';
import {
  HIDE_SYNC_LABEL,
  TimelineActor,
  isSyncChange,
  timelineEventWords,
  timelineField,
  timelineValue
} from '../components/timeline-values';
import { useResponseContext } from './response-view-page';

/**
 * The response's history, over four sources merged by the API: `audit_event` rows this
 * system wrote, `legacy_survey_log` rows the ETL landed verbatim from the old platform, the
 * staff fields' events recorded on this response (`internal`, SR-209 — sent since then, drawn
 * since SR-222, its field code in the Field column), and Salesforce's field history of the answer
 * fields it tracked (`salesforce`, SR-222's block 9 — a migrated response's alone, the
 * participant's latest of the survey).
 *
 * The native and legacy halves are disjoint in practice — the migration writes no audit events —
 * so in real data a response has one kind of row or the other (a migrated one Salesforce's rows
 * too), and which it has says where it came from. That is why every row is labelled with its
 * source rather than the distinction being smoothed away:
 *
 * - a legacy row carries no event type, its values are the old system's text exactly as it
 *   stored them (unnormalized, un-unescaped), and its actor is a legacy user id with no
 *   account behind it;
 * - `actorRole` is null on a legacy row and never `migration` — a person made that edit in
 *   the old system, and the migration only copied the record.
 *
 * `usage` is null where the legacy field map never resolved a question. That is normal, not
 * an error, and `legacyField` still names the old column — so the column shows the one that
 * is present rather than an empty cell (`timelineField`); a Salesforce row whose target the
 * response's version lacks is named by its field's label, else its API name.
 *
 * **The sync's Salesforce rows are hidden by default**, by a switch over the page read
 * (`isSyncChange`): they are the advisor's own saves, which the legacy rows already show, and
 * survey-api marks them without filtering. What the switch took is said beside it; the pager and
 * `total` stay survey-api's, so a page can hold fewer rows than it counts. Switched off, each is
 * marked "sync" beside its source.
 *
 * Values and actors are drawn by `timeline-values.tsx`, the one rendering this tab shares with a
 * question's history on the Answers tab: a native value parsed from its canonical JSON, the other
 * three as sent. A correction's reason (`answer_corrected`) is said under its event type, where
 * staff gave one, and the list batch a correction was made in (SR-403) under that (`withBatch`).
 */
export function AdminResponseTimelinePage() {
  const { responseId = '' } = useParams();
  const [source, setSource] = React.useState(ALL);
  const [page, setPage] = React.useState(1);
  const [hidingSync, setHidingSync] = React.useState(true);

  // Read again when the revision moves: a recycle bin move pressed in the page's header (SR-470)
  // writes a row of its own while this pane is open.
  const { revision } = useResponseContext().response;
  const { data, error } = useAdminData<IPage<ITimelineRow>>(
    (signal) =>
      oversightApi.timeline(
        responseId,
        { source: source === ALL ? undefined : (source as TimelineSource), page },
        signal
      ),
    [responseId, source, page, revision]
  );

  return (
    <>
      {error && <LoadError message={error} />}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Newest first. Undated legacy rows sort last rather than first.
        </p>
        <Select
          value={source}
          onValueChange={(value) => {
            setSource(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-48" aria-label="Filter by source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All sources</SelectItem>
            <SelectItem value="native">This system</SelectItem>
            <SelectItem value="legacy">Legacy platform</SelectItem>
            <SelectItem value="internal">Staff fields</SelectItem>
            <SelectItem value="salesforce">Salesforce</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {data === null ? (
        <TableSkeleton />
      ) : (
        <Rows data={data} hidingSync={hidingSync} onHidingSync={setHidingSync} onPage={setPage} />
      )}
    </>
  );
}

function Rows({
  data,
  hidingSync,
  onHidingSync,
  onPage
}: {
  data: IPage<ITimelineRow>;
  hidingSync: boolean;
  onHidingSync: (hiding: boolean) => void;
  onPage: (page: number) => void;
}) {
  const syncable = data.items.some(isSyncChange);
  const shown = hidingSync ? data.items.filter((row) => !isSyncChange(row)) : data.items;
  return (
    <>
      {syncable && (
        <HiddenRows
          label={HIDE_SYNC_LABEL}
          hiding={hidingSync}
          onHiding={onHidingSync}
          hidden={data.items.length - shown.length}
        />
      )}
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">When</TableHead>
              <TableHead className="w-40">What</TableHead>
              <TableHead>Field</TableHead>
              <TableHead>From</TableHead>
              <TableHead>To</TableHead>
              <TableHead className="w-40">Who</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((row) => (
              <TableRow key={`${row.source}-${row.id}`}>
                <TableCell className="text-xs text-muted-foreground">
                  {row.occurredAt ? formatDateTime(row.occurredAt) : 'undated'}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{row.source}</Badge>
                  {row.eventType && (
                    <span className="ml-2 text-xs text-muted-foreground">{row.eventType}</span>
                  )}
                  {/* SR-416's kinds in words (F6), the stored kind kept beside. */}
                  {timelineEventWords(row) && (
                    <span className="mt-1 block text-sm" data-event-words>
                      {timelineEventWords(row)}
                    </span>
                  )}
                  {isSyncChange(row) && (
                    <span className="ml-2 text-xs text-muted-foreground">sync</span>
                  )}
                  {row.reason != null && (
                    <span className="mt-1 block text-xs whitespace-pre-line">
                      Reason: {row.reason}
                    </span>
                  )}
                  {withBatch(null, row.batch)}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {timelineField(row) ?? '—'}
                  {row.instanceKey !== '' && (
                    <span className="block font-sans text-[11px] text-muted-foreground">
                      {row.instanceKey}
                    </span>
                  )}
                </TableCell>
                <TableCell className="max-w-56 text-sm break-words text-muted-foreground">
                  {timelineValue(row, row.oldValue)}
                </TableCell>
                <TableCell className="max-w-56 text-sm break-words">
                  {timelineValue(row, row.newValue)}
                </TableCell>
                <TableCell className="text-xs break-all text-muted-foreground">
                  <TimelineActor row={row} />
                </TableCell>
              </TableRow>
            ))}
            {data.items.length > 0 && shown.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  Every row on this page is a sync change — the advisor's own save, shown by
                  the legacy rows.
                </TableCell>
              </TableRow>
            )}
            {data.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  No history under this filter. A response filled in this system and never
                  touched since carries only the events its own writes produced.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pager page={data.page} pageSize={data.pageSize} total={data.total} onPage={onPage} />
    </>
  );
}

const ALL = 'all';
